import * as React from 'react';
import Box from '@mui/material/Box';
import TextField from '@mui/material/TextField';
import Autocomplete from '@mui/material/Autocomplete';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import parse from 'autosuggest-highlight/parse';
import { debounce } from '@mui/material/utils';
import { Controller } from 'react-hook-form';
import { FormControl, FormHelperText, FormLabel } from '@mui/material';
import Images from '../Utils/images';
import useGoogleMapLoad from './useGoogleMapLoad';

// You need to create a new one for your application.
const GOOGLE_MAPS_API_KEY = 'AIzaSyCu3pQ2rxalRAWIONNcxoYST8Cy7NJrveA';

function loadScript(src: string, position: HTMLElement | null, id: string) {
  if (!position) {
    return;
  }

  const script = document.createElement('script');
  script.setAttribute('async', '');
  script.setAttribute('id', id);
  script.src = src;
  position.appendChild(script);
}

const autocompleteService = { current: null };

interface MainTextMatchedSubstrings {
  offset: number;
  length: number;
}
interface StructuredFormatting {
  main_text: string;
  secondary_text: string;
  main_text_matched_substrings?: readonly MainTextMatchedSubstrings[];
}
interface PlaceType {
  description: string;
  structured_formatting: StructuredFormatting;
}
interface Props {
  id?: string;
  labelText?: string;
  placeHolderText?: string;
  name?: string;
  error?: boolean;
  helperText?: string;
  defaultValue?: string | any;
  disabled?: boolean;
  typedValue?: any;
  onChange?: any;
  options?: any;
  control?: null | any;
  setValue?: any;
  isError?: boolean;
  isCountryField?: boolean;
  multiple?: boolean;
  isCheckbox?: boolean;
  requiredField?: boolean;
  labelKey?: string;
  limit?: number;
  clearIcon?: boolean;
  disableClearable?: boolean;
  onInputChange?: any;
  setLatLong?: any;
  changeCallBack?: any;
  defaultInputVal?: string;
  isObject?: boolean;
  searchType?: 'place' | 'country';
  citiesWithCountry?: string | undefined;
}
/**
 * AutoCompleteLocation component for location input with Google Places autocomplete service.
 * @example
 * <AutoCompleteLocation
 *   id="location-input"
 *   labelText="Enter Location"
 *   placeHolderText="Search for a place"
 *   name="location"
 *   control={control}
 *   setValue={setValue}
 *   isCountryField={true}
 *   searchType="country"
 * />
 * @param {Props} props - The properties object containing various configuration options for the component.
 * @param {string} props.id - Identifier for the input field.
 * @param {string} props.labelText - Text label for the input field.
 * @param {string} props.placeHolderText - Placeholder text for the input field.
 * @param {string} [props.name=''] - Name attribute for the input field.
 * @param {boolean} [props.error] - Boolean indicating whether the input has an error.
 * @param {string} [props.helperText] - Helper text displayed below the input field.
 * @param {PlaceType} [props.defaultValue] - Default value for the input field.
 * @param {boolean} [props.disabled] - Disabled state for the input field.
 * @param {Function} [props.control] - React-Hook-Form control object.
 * @param {Function} props.setValue - Function to set the input value.
 * @param {boolean} [props.isCountryField=false] - Determines if the input is for country selection.
 * @param {boolean} [props.multiple] - Allows multiple selection if true.
 * @param {boolean} [props.isCheckbox] - Determines if a checkbox is associated with input.
 * @param {boolean} [props.requiredField] - Marks the field as required.
 * @param {Function} [props.onChange] - Callback function triggered on input value change.
 * @param {string} [props.labelKey] - Key used to display label.
 * @param {number} [props.limit=1] - Limit for autocomplete suggestions.
 * @param {boolean} [props.clearIcon=false] - Displays clear icon if true.
 * @param {boolean} [props.disableClearable=false] - Disables clearing the input if true.
 * @param {Function} [props.onInputChange] - Function called during input changes.
 * @param {boolean} [props.setLatLong=false] - Allows setting latitude and longitude from selection.
 * @param {Function} [props.changeCallBack] - Callback executed on change.
 * @param {string} [props.defaultInputVal] - Default input value as string.
 * @param {boolean} [props.isObject] - Determines if value is managed as an object.
 * @param {string} [props.searchType='place'] - Type of search such as 'place' or 'country'.
 * @param {string} [props.citiesWithCountry] - Country code for city search restrictions.
 * @returns {JSX.Element} A FormControl JSX element used for location input with autocomplete features.
 * @description
 *   - Contains a debounced fetch function for Google Places API to update suggestions.
 *   - Handles Google Maps API loading and error display.
 *   - Updates options and maintains input value, including geocoding for latitude and longitude.
 *   - Custom rendering to handle matched substrings from autocomplete suggestions.
 */
export default function AutoCompleteLocation(props: Props) {
  const {
    id,
    labelText,
    placeHolderText,
    name = '',
    error,
    helperText,
    defaultValue,
    disabled,
    control,
    setValue,
    isCountryField = false,
    multiple,
    isCheckbox,
    requiredField,
    onChange,
    labelKey,
    limit = 1,
    clearIcon = false,
    disableClearable = false,
    onInputChange,
    setLatLong = false,
    changeCallBack,
    defaultInputVal,
    isObject,
    searchType = 'place', // Default to 'place'
    citiesWithCountry = undefined,
  } = props;

  const [typedValue, setTypedValue] = React.useState<PlaceType | null>(
    defaultValue,
  );

  const [inputValue, setInputValue] = React.useState(defaultInputVal);
  const [options, setOptions] = React.useState<readonly PlaceType[]>([]);
  const [isGoogleMapLoad] = useGoogleMapLoad();

  // const loaded = React.useRef(false)

  // // if (typeof window !== 'undefined' && !loaded.current) {
  // if (isGoogleMapLoad) {
  //   //   loadScript(
  //   //     `https://maps.googleapis.com/maps/api/js?key=${GOOGLE_MAPS_API_KEY}&libraries=places`,
  //   //     document.querySelector('head'),
  //   //     'google-maps'
  //   //   )
  //   // }

  //   loaded.current = true
  // }
  // `https://maps.googleapis.com/maps/api/place/details/json?key=${SERVICE_URLS.GOOGLE_API_KEY}&place_id=${placeId}`
  const fetch = React.useMemo(
    () =>
      debounce(
        (
          request: { input: string },
          callback: (results?: readonly PlaceType[]) => void,
        ) => {
          if (autocompleteService.current) {
            const requestOptions = {
              ...request,
              types:
                searchType === 'country'
                  ? ['(regions)']
                  : citiesWithCountry
                  ? ['(cities)']
                  : undefined,
              componentRestrictions: citiesWithCountry
                ? { country: citiesWithCountry }
                : {},
            };

            (autocompleteService.current as any).getPlacePredictions(
              requestOptions,
              callback,
            );
          }
        },
        400,
      ),
    [searchType, citiesWithCountry],
  );
  // React.useEffect(() => {
  //   if (typedValue == null && defaultValue) {
  //     setTypedValue(defaultValue)
  //     setOptions([defaultValue])
  //     setInputValue(defaultValue.description)
  //   }
  // }, [defaultValue])

  React.useEffect(() => {
    if (defaultValue) {
      setTypedValue(defaultValue);
      setInputValue(defaultValue.description || ''); // In case it's just a string
      // setOptions((prevOptions) => {
      //   const exists = prevOptions.some(
      //     (opt) => opt.description === defaultValue.description
      //   )
      //   return exists ? prevOptions : [defaultValue, ...prevOptions]
      // })
    } else {
      setTypedValue(null);
      setInputValue('');
    }
  }, [defaultValue]);

  React.useEffect(() => {
    let active = true;
    if (!(window as any).google) {
      console.error('Google Maps API is not loaded');
      return;
    }

    if (!autocompleteService?.current && (window as any).google) {
      autocompleteService.current = new (
        window as any
      ).google.maps.places.AutocompleteService();
    }
    if (!autocompleteService?.current) {
      return undefined;
    }

    if (inputValue === '') {
      setOptions(typedValue ? [typedValue] : []);
      return undefined;
    }

    fetch({ input: inputValue }, (results?: readonly PlaceType[]) => {
      if (active) {
        let newOptions: readonly PlaceType[] = [];

        if (typedValue) {
          newOptions = [typedValue];
        }

        if (results) {
          newOptions = [...newOptions, ...results];
        }

        setOptions(newOptions);
      }
    });

    return () => {
      active = false;
    };
  }, [typedValue, inputValue, fetch]);

  const [open, setOpen] = React.useState(true);
  return (
    <FormControl
      fullWidth
      className="customAutocomplete location_inp"
      error={error ? true : false}
      id={id}
    >
      <FormLabel className="formLabel">
        {labelText} {requiredField ? <sup>*</sup> : null}{' '}
      </FormLabel>
      {control ? (
        <Controller
          name={name}
          control={control}
          defaultValue={defaultValue ? defaultValue : null}
          render={({ field }) => {
            return (
              <Autocomplete
                // open={open}
                // onOpen={() => setOpen(true)}
                // onClose={() => setOpen(true)}
                popupIcon={<img src={Images.SEARCH_BLACK} alt="Search" />}
                className="disablePopUpIndicator"
                getOptionLabel={option =>
                  typeof option === 'string' ? option : option.description
                }
                filterOptions={x => x}
                options={options}
                placeholder={placeHolderText}
                autoComplete={false} // Disable browser autofill
                {...field}
                value={typedValue}
                filterSelectedOptions
                noOptionsText="No locations"
                onChange={(event: any, newValue: PlaceType | null) => {
                  if (newValue) {
                    const geocoder = new window.google.maps.Geocoder();
                    // Geocode the place using Place ID
                    geocoder.geocode(
                      { placeId: newValue.place_id },
                      (
                        results: google.maps.GeocoderResult[],
                        status: google.maps.GeocoderStatus,
                      ) => {
                        if (
                          status === google.maps.GeocoderStatus.OK &&
                          results[0]
                        ) {
                          const place = results[0];
                          changeCallBack && changeCallBack(place, newValue);

                          // Extract latitude and longitude from the geocoded place result
                          const location = place.geometry?.location;

                          const lat = location?.lat();
                          const lng = location?.lng();
                          // Include latitude and longitude in the value
                          const valueWithLatLng = {
                            description:
                              citiesWithCountry &&
                              newValue.structured_formatting &&
                              newValue.structured_formatting.main_text
                                ? newValue.structured_formatting.main_text
                                : newValue.description,
                            latitude: lat,
                            longitude: lng,
                            structured_formatting:
                              newValue.structured_formatting,
                          };

                          // Update options and typedValue
                          setOptions([valueWithLatLng, ...options]);
                          setTypedValue(valueWithLatLng);
                          if (setLatLong && valueWithLatLng.latitude) {
                            setLatLong({
                              lng: valueWithLatLng.longitude,
                              lat: valueWithLatLng.latitude,
                            });
                          }
                          // Call onChange with the value including latitude and longitude
                        } else {
                          console.error(
                            'Geocode was not successful for the following reason:',
                            status,
                          );
                        }
                      },
                    );
                    isObject
                      ? setValue(name, newValue)
                      : setValue(name, newValue?.description);
                  } else {
                    // If newValue is null, clear the typedValue and call onChange with null
                    setTypedValue(null);
                    setValue(name, null);
                    changeCallBack && changeCallBack(event, newValue);
                  }

                  // ✅ Blur after selection
                  // setTimeout(() => {
                  const activeEl = document.activeElement as HTMLElement;
                  if (activeEl) activeEl.blur();
                  // }, 0)

                  // Update form value if using React Hook Form
                }}
                onInputChange={(event, newInputValue) => {
                  setInputValue(newInputValue);
                }}
                size="small"
                renderInput={params => (
                  <TextField
                    {...params}
                    value={inputValue}
                    fullWidth
                    placeholder={placeHolderText}
                    size="small"
                    autoComplete="new-city" // Use a non-standard value
                    error={error ? true : false}
                  />
                )}
                renderOption={(props, option) => {
                  const matches =
                    option?.structured_formatting
                      ?.main_text_matched_substrings || [];

                  const parts = parse(
                    option?.structured_formatting?.main_text,
                    matches.map((match: any) => [
                      match.offset,
                      match.offset + match.length,
                    ]),
                  );

                  return (
                    <li {...props}>
                      <Grid container alignItems="center">
                        <Grid item sx={{ display: 'flex', width: 44 }}>
                          <LocationOnIcon sx={{ color: 'text.secondary' }} />
                        </Grid>
                        <Grid
                          item
                          sx={{
                            width: 'calc(100% - 44px)',
                            wordWrap: 'break-word',
                          }}
                        >
                          {parts.map((part, index) => (
                            <Box
                              key={index}
                              component="span"
                              sx={{
                                fontWeight: part.highlight ? 'bold' : 'regular',
                              }}
                            >
                              {part.text}
                            </Box>
                          ))}
                          <Typography variant="body2" color="text.secondary">
                            {option?.structured_formatting?.secondary_text}
                          </Typography>
                        </Grid>
                      </Grid>
                    </li>
                  );
                }}
              />
            );
          }}
        />
      ) : null}

      <FormHelperText className={error ? 'Mui-error' : ''}>
        {helperText}
      </FormHelperText>
    </FormControl>
  );
}
